import { TriangleAlert } from "lucide-react";
import { useState, type FormEvent } from "react";

import {
  formatMeasurementRange,
  measurementRange,
  parseDecimalInput,
  type MeasurementField,
} from "../../shared/measurements";
import { rangeWarning } from "../../shared/pool-thresholds";
import { trpc } from "../lib/trpc";

/**
 * Inserimento rapido di una misurazione, pensato per il telefono.
 *
 * Chi lo usa ha l'analisi in mano e una mano sola libera: tastiera numerica su ogni campo
 * (`inputMode="decimal"`, che apre il tastierino senza rifiutare la virgola) e un solo tocco
 * per salvare. Niente conferme intermedie.
 *
 * Un valore fuori intervallo **avvisa e basta**: il pulsante non si spegne mai. E la stessa
 * regola del registro piscine, e l'avviso lo scrive la stessa funzione: due testi diversi per
 * lo stesso fatto confonderebbero chi legge.
 */
export function MeasurementForm({
  interventionId,
  activityId,
  activityLabel,
  fields,
  onSaved,
  onCancel,
}: {
  interventionId: string;
  activityId: string;
  activityLabel: string;
  fields: MeasurementField[];
  onSaved?: () => void;
  onCancel?: () => void;
}) {
  const [avvisi, setAvvisi] = useState<string[]>([]);
  const [errore, setErrore] = useState<string | null>(null);
  const record = trpc.measurements.record.useMutation();

  function avvisoDi(campo: MeasurementField, valore: number | null) {
    const range = measurementRange(campo);
    if (!range) return null;
    return rangeWarning(campo.label, valore, range, campo.unit ? ` ${campo.unit}` : "");
  }

  function ricalcolaAvvisi(letti: Array<{ campo: MeasurementField; valore: number | null }>) {
    return letti
      .map(({ campo, valore }) => avvisoDi(campo, valore))
      .filter((avviso): avviso is string => Boolean(avviso));
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrore(null);
    const form = event.currentTarget;
    // I valori si rileggono dal modulo vero con FormData: dettatura e incolla scrivono nel
    // campo senza emettere l'evento che aggiornerebbe lo stato React.
    const data = new FormData(form);
    const letti = fields.map((campo) => ({ campo, valore: parseDecimalInput(String(data.get(campo.key) ?? "")) }));
    const values = letti
      .filter((riga): riga is { campo: MeasurementField; valore: number } => riga.valore != null)
      .map(({ campo, valore }) => ({ key: campo.key, value: valore }));
    setAvvisi(ricalcolaAvvisi(letti));
    if (values.length === 0) {
      setErrore("Scrivi almeno un valore.");
      return;
    }
    try {
      await record.mutateAsync({
        interventionId,
        activityId,
        values,
        notes: String(data.get("notes") ?? "").trim() || null,
      });
      form.reset();
      onSaved?.();
    } catch (error) {
      setErrore(error instanceof Error ? error.message : "Registrazione non riuscita.");
    }
  }

  return (
    <form className="measurement-form" onSubmit={submit}>
      <div className="measurement-form__heading">
        <p className="eyebrow">Misurazione</p>
        <h3>{activityLabel}</h3>
      </div>
      <div className="form-grid">
        {fields.map((campo) => (
          <label key={campo.key}>
            <span>{campo.unit ? `${campo.label} in ${campo.unit}` : campo.label}</span>
            <input
              name={campo.key}
              inputMode="decimal"
              autoComplete="off"
              placeholder={formatMeasurementRange(campo) ?? ""}
              onInput={(event) => setAvvisi(ricalcolaAvvisi([{ campo, valore: parseDecimalInput(event.currentTarget.value) }]))}
            />
          </label>
        ))}
        <label className="form-grid__wide"><span>Note</span><input name="notes" placeholder="Acqua torbida, filtro pulito" /></label>
      </div>
      {avvisi.map((avviso) => (
        <p className="pool-warning" role="status" key={avviso}><TriangleAlert size={17} aria-hidden="true" /> {avviso}</p>
      ))}
      {errore && <p className="form-error" role="alert">{errore}</p>}
      <div className="form-actions">
        {onCancel && <button type="button" className="button button--ghost" onClick={onCancel}>Annulla</button>}
        <button className={`button button--primary${record.isPending ? " button--saving" : ""}`}>
          {record.isPending ? "Salvataggio..." : "Salva la misurazione"}
        </button>
      </div>
    </form>
  );
}
